<!--
  ContactInfo.vue

  this component have required props,
    - 'initialState': which should be an array of object.
    - 'labelText': text label for the group fields
    - 'phoneNumberTypeList': list of available options with key-value pairs
       e.g. { returnValue: 'Mobile', display: 'Mobile' }

  'initialState' contains an array of objects which should contain following keys:
    - type: this will be used as phone number type and text of option
    - number: the number value user enters as contact number

  additionally, it can have optional props
  - 'getRequired' must be a boolean type, which will be used as 'required' prop value for field
  - 'componentName' that is only if we want to do specific $emit for a component
  - 'id' if want to add more specific id to FormField
-->

<template>
  <FormField :id="`prefphoneno-${id}`" :labelText="labelText" :required="getRequired" :helperText="helperText">
    <div class="contact-info">
      <div v-for="(contact, index) in preferredContactMethods" :key="index">
        <div class="contact-row">
          <DropdownSelect
            :disabled="isDisabled || isTypeReadOnly(contact.type)"
            :id="`${contact.type}-phone-dropdown`"
            name="phone-dropdown"
            :items="getSelectOptionsLeft(index, preferredContactMethods, phoneNumberTypeList, contact.type)"
            v-model.lazy="contact.type"
            :initialSelect="
              getRequired || index > 0 || contact.type !== '' ? getDisplayValue(contact.type, phoneNumberTypeList) || filteredReturnValue[0].returnValue : null
            "
            :noDisabled="getRequired || index > 0"
          />
          <FormInput
            :disabled="isDisabled || isTypeReadOnly(contact.type)"
            :id="`contact-type-${contact.type}`"
            type="tel"
            autocomplete="tel"
            v-model.lazy="contact.number"
            :placeholder="getNumberPlaceholder(contact.type)"
            maxlength="12"
            :overrideClass="v$.preferredContactMethods.$each.$response?.$data[index].number.$error ? 'error' : null"
            :aria-labelledby="contact.type"
            v-maska="getNumberMask(contact.number, contact.type)"
          />
        </div>
        <!-- Error message -->
        <label v-if="!isTypeReadOnly(contact.type) && (v$.preferredContactMethods.$each.$response?.$data[index].number.$error || v$.preferredContactMethods.$each.$response?.$data[index].type.$invalid)" class="error" :for="`contact-type-${index}`">
          {{
            v$.preferredContactMethods.$each.$response?.$data[index].number.$error ? getContactErrorMessage(contact.number, contact.type) : v$.preferredContactMethods.$each.$response?.$data[index].type.$invalid ? 'Please select preferred phone number type.' : ''
          }}
        </label>
      </div>
      <template v-if="addMoreNumbers">
        <!--  Add number    -->
        <a v-if="(preferredContactMethods.length > 1) & !isDisabled & !lastContactMethodReadOnly" @click.prevent="removeNumber" role="button" href="#">Remove number</a>
        <!--  Remove number    -->
        <a v-if="(preferredContactMethods.length < phoneNumberTypeList.length) & !isDisabled" @click.prevent="addNumber" role="button" href="#">
          Add an additional phone number
        </a>
      </template>
    </div>
  </FormField>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { FormField, FormInput } from '@/components';
import { getContactErrorMessage, getEmailErrorMessage } from '@/utils/ErrorMessages';
import { OperationType } from '@/enums';
import { getNumberMask, getNumberPlaceholder, validContactNumber } from '@/utils';
import { helpers } from '@vuelidate/validators';

export default {
  name: 'ContactInfo',
  setup() { return { v$: useVuelidate() }; },

  components: {
    DropdownSelect,
    FormInput,
    FormField,
  },

  props: {
    /**
     * defines initial state of preferred contact methods.
     * it should be array of object, where obj is { type: string, number: string}
     */
    initialState: {
      type: Array,
    },
    /**
     * list of available options with key-value pairs
     * e.g. { returnValue: 'Mobile', display: 'Mobile' }
     */
    phoneNumberTypeList: {
      type: Array,
      required: true,
    },
    /**
     * which should be an object model.
     */
    getRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * text label for the group fields
     */
    labelText: {
      type: String,
      required: true,
    },
    /**
     * component name if we want to $emit so based on the component $emit will happen
     * and it's optional
     */
    componentName: {
      required: false,
      default: '',
    },
    /**
     * id
     */
    id: {
      type: String,
      default: '',
    },
    /**
     * addMoreNumbers if need to have add more number options
     */
    addMoreNumbers: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    // Phone types to render as read-only text instead of an editable input.
    readOnlyTypes: {
      type: Array,
      default: () => [],
    },
    // Text shown above the contact fields, e.g. an account security notice.
    helperText: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      // default to the first phone type (e.g. Mobile) so the dropdown is never left blank
      preferredContactMethods: [
        {
          type: this.phoneNumberTypeList[0].returnValue,
          number: '',
        },
      ],
      availablePhoneTypeList: [],
      allocatedPhoneTypeList: [],
      dropDownCounter: 1,
      filteredReturnValue: null,
      suppressCommunicationFlag: false,
    };
  },

  beforeMount() {
    if (this.initialState && this.initialState.length > 0) {
      const arr = [];
      this.initialState.forEach((obj) => {
        arr.push({ ...obj });
      });
      this.preferredContactMethods = arr;
      this.dropDownCounter = this.initialState.length;
    }
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    // guards "Remove number": removeNumber() always removes the last entry, so only that entry's lock state matters
    lastContactMethodReadOnly() {
      const lastContact = this.preferredContactMethods[this.preferredContactMethods.length - 1];
      return lastContact ? this.isTypeReadOnly(lastContact.type) : false;
    },
    validationObj() {
      // read-only (e.g. MFA-locked) entries can't be edited by the member, so they must be excluded from validation
      const self = this;
      const obj = {
        preferredContactMethods: {
          allInput(eachArr) {
            const editableEntries = eachArr.filter((eachObj) => {
              return !self.isTypeReadOnly(eachObj.type);
            });

            // if member added new number but did not put input, will return false, which will mark preferredContact invalid
            if (editableEntries.length > 1) {
              return editableEntries.every((eachObj) => {
                return eachObj.type !== '' && eachObj.number !== '';
              });
            }

            // if only one editable preferredContact, return true to mark this validation pass
            return true;
          },
          $each: helpers.forEach({
            type: {
              required: requiredIf(function (contactNumberObject) {
                return !self.isTypeReadOnly(contactNumberObject.type) && contactNumberObject.number !== '';
              }),
            },
            number: {
              required: requiredIf(function (contactNumberObject) {
                return !self.isTypeReadOnly(contactNumberObject.type) && contactNumberObject.type !== '';
              }),
              isValidContactNumber: (number, pair) => {
                return self.isTypeReadOnly(pair.type) || validContactNumber(number, pair.type) || number === '';
              },
            },
          }),
        },
      };

      if (this.getRequired) {
        obj.preferredContactMethods = {
          ...obj.preferredContactMethods,
          required,
          $each: helpers.forEach({
            type: {
              ...obj.preferredContactMethods.$each.type,
            },
            number: {
              ...obj.preferredContactMethods.$each.number,
              isValidContactNumber: (number, pair) => {
                return self.isTypeReadOnly(pair.type) || validContactNumber(number, pair.type);
              },
            },
          }),
        };
      }

      return obj;
    },
  },

  watch: {
    v$: {
      handler(oldValue, newValue) {
        if (this.componentName === 'ContactInformation') {
          if (oldValue !== newValue) {
            this.$emit('updateSuppressCommunicationFlag', true);
          }
          /* update the validation as isInvalid is sending false even the preferredContactMethods is invalid */
          if (this.v$) {
            const returnObj = {
              isInvalid: this.v$.$invalid,
              phoneNumbersArr: this.preferredContactMethods,
            };
            this.$emit('updatePreferredContactMethods', returnObj);
          }
        }
      },
      deep: true,
    },

    preferredContactMethods: {
      handler(newArr) {
        const returnObj = {
          isInvalid: this.v$.$invalid,
          phoneNumbersArr: newArr,
        };
        this.$emit('updatePreferredContactMethods', returnObj);
      },
      deep: true,
    },
  },

  methods: {
    getEmailErrorMessage,
    getContactErrorMessage,
    getNumberPlaceholder,
    getNumberMask,

    isTypeReadOnly(type) {
      return this.readOnlyTypes.some((readOnlyType) => {
        return readOnlyType?.toLowerCase() === type?.toLowerCase();
      });
    },

    // this is a used as common function
    // update the preferred contact method based on the add/remove click
    // "availablePhoneTypeList: phone types available to choose from, "allocatedPhoneTypeList": allocated phone types"
    updatePreferredContact(operation, options, index) {
      if (operation === OperationType.ADD) {
        this.allocatedPhoneTypeList.push(this.preferredContactMethods[index].type);
        this.availablePhoneTypeList = options.filter((item) => {
          return !this.allocatedPhoneTypeList.includes(item.returnValue);
        });
        this.availablePhoneTypeList = JSON.parse(JSON.stringify(this.availablePhoneTypeList));
      }
      if (operation === OperationType.REMOVE) {
        this.availablePhoneTypeList.unshift({ type: options[1].returnValue });
        this.allocatedPhoneTypeList = this.allocatedPhoneTypeList.filter((item) => {
          return item !== options[1].display;
        });
        this.preferredContactMethods.splice(options.length - 1, 1);
      }
    },

    removeNumber() {
      const selectedOptions = this.preferredContactMethods;
      if (this.dropDownCounter === 3) {
        this.updatePreferredContact(OperationType.REMOVE, selectedOptions, 1);
      }
      if (this.dropDownCounter === 2) {
        this.updatePreferredContact(OperationType.REMOVE, selectedOptions, 0);
      }
      this.dropDownCounter -= 1;
    },

    addNumber() {
      this.dropDownCounter += 1;

      if (this.dropDownCounter === 2) {
        this.updatePreferredContact(OperationType.ADD, this.phoneNumberTypeList, 0);
        this.preferredContactMethods.push({
          type: this.filteredReturnValue[0].display,
          number: '',
        });
      }
      if (this.dropDownCounter === 3) {
        this.updatePreferredContact(OperationType.ADD, this.phoneNumberTypeList, 1);
        this.preferredContactMethods.push({
          type: this.filteredReturnValue[0].display,
          number: '',
        });
      }
    },

    // get the actual display value
    getDisplayValue(modelValue, listName) {
      return listName.find((el) => {
        return el.returnValue?.toLowerCase() === modelValue?.toLowerCase();
      })?.display;
    },

    // To obtain a new list of select options after adding or update contact
    getSelectOptionsLeft(index, selectedOptions, allOptions, type) {
      const options = [];

      for (let i = 0; i < selectedOptions.length; i++) {
        if (i !== parseInt(index, 10)) {
          options.push(selectedOptions[i].type);
        }
      }
      const allOptionsReturnValue = allOptions.filter((el) => {
        return !options.includes(el.returnValue);
      });
      // will return filtered values that can be in dropdown list, then we use the first element as initial value for dropdown
      this.filteredReturnValue = allOptionsReturnValue.filter((el) => {
        return el.returnValue?.toLowerCase() !== type?.toLowerCase();
      });

      // transform data to key-value pairs of { name: '', value: '' } for dropdown component
      const transformedOptions = allOptions.map((tel) => {
        return {
          name: tel.display,
          value: tel.returnValue,
        };
      });

      return transformedOptions.filter((el) => {
        return !options.includes(el.value);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-info {
  display: grid;
  grid-template-columns: 1fr;
  gap: spacer(1.5);

  .contact-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: spacer(1.5);

    @media screen and (max-width: 768px) {
      .dropdown-select-wrapper {
        margin-bottom: 15px;
      }
    }
  }

  .remove-button {
    margin-top: spacer(1);
  }
}
</style>
